/**
 * Plant Doctor, as the remodelled landing page (landing.V2) has it: the same
 * worry, a new heading ("Find out what's wrong, and fix it.") and body, and
 * a button that says "Try Plant Doctor free". The evidence is a phone holding
 * Plant Doctor's real answer (public/landing/plant-doctor-answer.jpg, its
 * header reading Plant Doctor), with the owner's lettuce photo beside it as a
 * tilted snapshot captioned "Your photo, Sep 28" (public/landing/
 * plant-doctor-photo.jpg).
 *
 * The words say what Plant Doctor reads and tells; nothing here promises it
 * is always right.
 *
 * Words (en/es): landing.v2.plantDoctor.*, and landing.plantDoctor.pain,
 * .photoAlt, .answerAlt.
 *
 * @module PlantDoctorV2
 */

import React from 'react';
import { Stethoscope } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { FeatureBlock } from '../components/FeatureBlock';
import { PhoneFrame } from '../components/PhoneFrame';
import type { LandingSectionProps } from '../landingTypes';

/**
 * The Plant Doctor section.
 *
 * @param props.facts - The founding welcome, when there is one (a founder is offered the founding claim, not a trial).
 * @param props.onStart - Starts a garden (the "Try Plant Doctor free" button).
 */
export const PlantDoctorV2: React.FC<LandingSectionProps> = ({ facts, onStart }) => {
  const { reactTranslate: t } = useTranslation();
  return (
    <FeatureBlock
      icon={<Stethoscope className="h-8 w-8" strokeWidth={1.75} />}
      quote
      pain={t('landing.plantDoctor.pain')}
      title={t('landing.v2.plantDoctor.title')}
      line={t('landing.v2.plantDoctor.body')}
      action={<Button size="lg" onClick={onStart}>{t(facts.startKey ?? 'landing.v2.plantDoctor.cta')}</Button>}
      evidence={(
        <div className="relative pl-16">
          <PhoneFrame className="max-h-[560px]">
            <img src="/landing/plant-doctor-answer.jpg" alt={t('landing.plantDoctor.answerAlt')} loading="lazy" className="block w-full" />
          </PhoneFrame>
          {/* The gardener's own photo, pinned beside the answer like a snapshot. */}
          <figure className="absolute bottom-9 left-0 m-0 w-32 -rotate-[5deg] rounded-md bg-white px-1.5 pb-2 pt-1.5 shadow-[0_12px_28px_rgb(0_0_0/0.28)]">
            <img src="/landing/plant-doctor-photo.jpg" alt={t('landing.plantDoctor.photoAlt')} loading="lazy" className="block aspect-[3/4] w-full rounded-[3px] object-cover" />
            <figcaption className="mt-[5px] text-center font-sans text-[11px] font-semibold leading-tight text-[#65676B]">{t('landing.v2.plantDoctor.caption')}</figcaption>
          </figure>
        </div>
      )}
    />
  );
};
